import { Folder, LayoutGrid, Plus } from "lucide-react";
import type { ReferenceCollection } from "@/types/reference.type";
import { cn } from "@/utils/helpers";

/*
 * The collections row under the title (References / Collection chip, style B):
 * 44px glass capsules. All references leads with its grid glyph; a collection
 * carries a mosaic of two round thumbs, its name and its count in mono. The
 * open one is solid with an ink hairline. New collection is accent text.
 */

function coverPicture(item: { imageUrl: string | null; videoUrl: string | null }) {
  return item.imageUrl ?? null;
}

/** A collection's cover: its newest picture at `size`, radius 12, or a folder. */
export function CollectionCover({ collection, size }: { collection: ReferenceCollection; size: number }) {
  const item = collection.cover[0];
  const still = item ? coverPicture(item) : null;
  const style = { width: size, height: size };
  if (item && still) {
    return <img src={still} alt="" style={style} className="shrink-0 rounded-card object-cover" />;
  }
  if (item?.videoUrl) {
    return (
      <video
        src={item.videoUrl}
        muted
        playsInline
        preload="metadata"
        style={style}
        className="shrink-0 rounded-card bg-co-recess object-cover"
        aria-hidden
      />
    );
  }
  return (
    <span style={style} className="grid shrink-0 place-items-center rounded-card border border-co-line2" aria-hidden>
      <Folder className="size-[18px] text-co-faint" />
    </span>
  );
}

function Thumb({ item }: { item: ReferenceCollection["cover"][number] }) {
  const still = coverPicture(item);
  if (still) return <img src={still} alt="" className="size-[30px] rounded-full object-cover" />;
  if (item.videoUrl) {
    return (
      <video
        src={item.videoUrl}
        muted
        playsInline
        preload="metadata"
        className="size-[30px] rounded-full bg-co-recess object-cover"
        aria-hidden
      />
    );
  }
  return null;
}

export default function CollectionsRow({
  collections,
  allCount,
  openId,
  onOpen,
  onNew,
}: {
  collections: ReferenceCollection[];
  /** What All references counts (the wall's own total); null while unknown. */
  allCount: number | null;
  /** The collection open as the wall, or null for All references. */
  openId: string | null;
  onOpen: (collectionId: string | null) => void;
  /** Absent for a viewer: they browse and read, and make nothing. */
  onNew?: (anchor: HTMLElement) => void;
}) {
  return (
    <nav className="ref-collections" aria-label="Collections">
      <button
        type="button"
        className={cn("ref-chip co-glass-control", openId === null && "is-open")}
        aria-current={openId === null ? "page" : undefined}
        onClick={() => onOpen(null)}
      >
        <span className="grid size-[30px] place-items-center" aria-hidden>
          <LayoutGrid className="size-3.5 text-co-text2" />
        </span>
        <span className="ref-chip-name">All references</span>
        {allCount !== null ? <span className="ref-chip-count">{allCount}</span> : null}
      </button>
      {collections.map((collection) => {
        const thumbs = collection.cover.filter((c) => c.imageUrl || c.videoUrl).slice(0, 2);
        return (
          <button
            key={collection.id}
            type="button"
            className={cn("ref-chip co-glass-control", openId === collection.id && "is-open")}
            aria-current={openId === collection.id ? "page" : undefined}
            onClick={() => onOpen(collection.id)}
          >
            {thumbs.length > 0 ? (
              <span className="flex gap-0.5" aria-hidden>
                {thumbs.map((item) => (
                  <Thumb key={item.referenceId} item={item} />
                ))}
              </span>
            ) : (
              <span className="grid size-[30px] place-items-center" aria-hidden>
                <Folder className="size-3.5 text-co-faint" />
              </span>
            )}
            <span className="ref-chip-name">{collection.name}</span>
            <span className="ref-chip-count">{collection.count}</span>
          </button>
        );
      })}
      {onNew ? (
        <button type="button" className="ref-chip ref-chip--new" onClick={(e) => onNew(e.currentTarget)}>
          <Plus aria-hidden className="size-[13px]" />
          New collection
        </button>
      ) : null}
    </nav>
  );
}
